import {
  RFQ_GROUPS,
  RFQ_STAGE,
  roDate,
  siteLabel,
  todayIso,
  type RfqGroup,
  type RfqListItem,
  type RfqStage,
  type RfqState,
  type Site,
  t,
} from '@procumi/shared';
import { useEffect, useState } from 'react';
import { Avatar, SupplierStack } from '../components/Avatar';
import { StatusIcon, type StatusKind } from '../components/Panel';
import { TemplateImportDialog } from '../components/TemplateImportDialog';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import { api, errMsg } from '../lib/api';
import type { Route } from '../routes';

/**
 * Lista cererilor, adusă pe felii de la server.
 *
 * Până la paginare, pagina trăgea toate cererile și filtra în browser. Cu ani
 * de istoric nu mai merge: acum starea, șantierul și căutarea pleacă la server,
 * iar de acolo vine o felie plus semnul de carte pentru următoarea.
 *
 * Numerele de pe butoanele de stare vin tot de la server și țin cont de șantier
 * și de căutare, ca să nu scrie „Închise 30" când găsești două.
 */

/** Cât se aduce o dată: îndeajuns cât să umple ecranul. */
const PAGINA = 25;

/** Cât se așteaptă după ultima tastă înainte de a întreba serverul. */
const ASTEPTARE = 300;

/** Linkurile vechi (din panou) cer o stare; o traducem în contorul potrivit. */
const GRUP_DIN_STARE: Record<RfqState, RfqGroup> = {
  open: 'active',
  closed: 'closed',
  canceled: 'canceled',
};

/** Iconița de stare din dreptul titlului, aceeași familie ca pe „Azi”. */
const STAGE_ICON: Record<RfqStage, StatusKind> = {
  draft: 'new',
  waiting: 'wait',
  late: 'late',
  decide: 'decide',
  toOrder: 'order',
  approval: 'approve',
  ordered: 'done',
  closed: 'done',
  canceled: 'new',
};

/** Etapele în care termenul încă contează: acolo spunem cât a mai rămas. */
const TERMEN_VIU = new Set<RfqStage>(['draft', 'waiting', 'late']);

/** „azi”, „mâine”, „în 4 zile”, „trecut de 2 zile”. */
function catMaiE(deadline: string, azi: string): string {
  const zi = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
  const n = Math.round((zi(deadline) - zi(azi)) / 86_400_000);
  if (n === 0) return t('azi');
  if (n === 1) return t('mâine');
  if (n === -1) return t('trecut de ieri');
  if (n > 0) return t('în {0} zile', n);
  return t('trecut de {0} zile', -n);
}

export function RfqListPage({
  go,
  initialQ = '',
  initialState = 'open',
}: {
  go: (r: Route) => void;
  /** căutarea cu care se deschide lista (din panou: o categorie) */
  initialQ?: string;
  initialState?: RfqState;
}) {
  const [grup, setGrup] = useState<RfqGroup>(GRUP_DIN_STARE[initialState]);
  /** dialogul „Din șablonul Excel” */
  const [sablon, setSablon] = useState(false);
  const azi = todayIso();
  const [cauta, setCauta] = useState(initialQ);
  const [santier, setSantier] = useState('');
  /** Căutarea care a plecat efectiv la server, în urma tastelor. */
  const [cautat, setCautat] = useState(initialQ.trim());

  useEffect(() => {
    const t = setTimeout(() => setCautat(cauta.trim()), ASTEPTARE);
    return () => clearTimeout(t);
  }, [cauta]);

  // Șantierele pentru filtru se aduc separat: lista de cereri nu le mai are pe
  // toate, fiind tăiată în felii.
  const santiere = useAsync(() => api.sites({ includeClosed: true }), [], 'sites:all');
  const prima = useAsync(
    () => api.rfqs({ group: grup, today: azi, siteId: santier, q: cautat, limit: PAGINA }),
    [grup, santier, cautat, azi],
    `rfqs:${grup}:${santier}:${cautat}:${azi}`,
  );

  /** Feliile aduse cu „Arată mai multe", după prima. */
  const [urmatoare, setUrmatoare] = useState<RfqListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [aduc, setAduc] = useState(false);
  const [eroareMai, setEroareMai] = useState('');

  // O felie nouă de început șterge tot ce s-a adunat peste ea.
  useEffect(() => {
    setUrmatoare([]);
    setCursor(prima.data?.next ?? null);
    setEroareMai('');
  }, [prima.data]);

  const maiAdu = async () => {
    if (!cursor) return;
    setAduc(true);
    setEroareMai('');
    try {
      const felie = await api.rfqs({
        group: grup,
        today: azi,
        siteId: santier,
        q: cautat,
        cursor,
        limit: PAGINA,
      });
      setUrmatoare((v) => [...v, ...felie.rows]);
      setCursor(felie.next);
    } catch (e) {
      setEroareMai(errMsg(e));
    } finally {
      setAduc(false);
    }
  };

  const counts = prima.data?.groups;
  const total = prima.data?.total ?? 0;
  const rows = [...(prima.data?.rows ?? []), ...urmatoare];
  const filtrat = Boolean(santier) || cautat.length > 0;
  const siteList: Site[] = santiere.data ?? [];

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Cereri de ofertă')}</h1>
          <p>
            {t(
              'Fiecare cerere pleacă o dată către toți furnizorii aleși. Ofertele primite se adună într-un tabel comparativ.',
            )}
          </p>
        </div>
        <div className="acts">
          <button className="btn" onClick={() => setSablon(true)}>
            {t('Din șablonul Excel')}
          </button>
          <button className="btn primary" onClick={() => go({ view: 'new' })}>
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            {t('Cerere nouă')}
          </button>
        </div>
      </div>
      {sablon ? (
        <TemplateImportDialog
          onClose={() => setSablon(false)}
          onDone={(id) => {
            setSablon(false);
            go({ view: 'rfq', id });
          }}
        />
      ) : null}

      <div className="filters rq-tabs" role="group" aria-label={t('Etapa cererilor')}>
        {RFQ_GROUPS.map((g) => {
          const n = counts?.[g.id];
          const alert = (g.id === 'late' || g.id === 'decide') && Boolean(n);
          return (
            <button
              key={g.id}
              type="button"
              aria-pressed={grup === g.id}
              className={n === 0 && grup !== g.id ? 'zero' : alert ? 'alert' : undefined}
              onClick={() => setGrup(g.id)}
            >
              {g.label}
              <span className="n">{n ?? '—'}</span>
            </button>
          );
        })}
      </div>

      <section className="sheet rq-panel">
        <div className="rq-tools">
          <label className="rq-search">
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="m10.5 10.5 3.2 3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              placeholder={t('Caută după titlu sau categorie…')}
              value={cauta}
              onChange={(e) => setCauta(e.target.value)}
              aria-label={t('Caută o cerere')}
            />
          </label>
          {siteList.length > 1 ? (
            <select
              className="inp rq-site"
              value={santier}
              onChange={(e) => setSantier(e.target.value)}
              aria-label={t('Filtrează după șantier')}
            >
              <option value="">{t('Toate șantierele')}</option>
              {siteList.map((s) => (
                <option key={s.id} value={s.id}>
                  {siteLabel(s)}
                </option>
              ))}
            </select>
          ) : null}
          {prima.data ? (
            <span className="rq-count">
              {rows.length === total
                ? `${total} ${total === 1 ? t('cerere') : t('cereri')}`
                : t('{0} din {1}', rows.length, total)}
            </span>
          ) : null}
        </div>

        {prima.loading ? <Loading /> : null}
        {!prima.loading && prima.error ? <ErrorBox error={prima.error} onRetry={prima.reload} /> : null}

        {!prima.loading && !prima.error ? (
          rows.length ? (
            <table className="rq-table">
              <thead>
                <tr>
                  <th>{t('Cerere')}</th>
                  <th>{t('Termen')}</th>
                  <th>{t('Furnizori')}</th>
                  <th>{t('Furnizor ales')}</th>
                  <th>{t('Făcută de')}</th>
                  <th className="r">{t('Etapa')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const stage = (r.stage ?? 'waiting') as RfqStage;
                  const open = () => go({ view: 'rfq', id: r.id });
                  const viu = TERMEN_VIU.has(stage);
                  const trecut = viu && r.deadline < azi;
                  const suppliers = r.sends.flatMap((x) =>
                    x.supplier ? [{ ...x.supplier, answered: x.hasOffer }] : [],
                  );
                  return (
                    <tr key={r.id} onClick={open} className={stage === 'canceled' ? 'off' : undefined}>
                      <td className="rq-what">
                        <div className="rq-what-in">
                          <StatusIcon kind={STAGE_ICON[stage]} />
                          <span>
                            <button
                              type="button"
                              className="rq-title"
                              onClick={(e) => (e.stopPropagation(), open())}
                            >
                              {r.title}
                            </button>
                            <span className="rq-sub">
                              {r.category}
                              <i aria-hidden="true">·</i>
                              {siteLabel(r.site)}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className={`rq-due${trecut ? ' late' : ''}`}>
                        {roDate(r.deadline)}
                        {viu ? <small>{catMaiE(r.deadline, azi)}</small> : null}
                      </td>
                      <td className="rq-sups">
                        {suppliers.length ? (
                          <SupplierStack suppliers={suppliers} />
                        ) : (
                          <span className="muted">{t('niciun furnizor')}</span>
                        )}
                      </td>
                      <td className="rq-chosen">
                        {r.chosen ? (
                          <span className="rq-who">
                            <Avatar name={r.chosen.supplier.name} />
                            <span>
                              {r.chosen.supplier.name}
                              {r.chosen.split?.length ? <small> +{r.chosen.split.length - 1}</small> : null}
                            </span>
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="rq-by">
                        {r.createdBy ? (
                          <span className="rq-who">
                            <Avatar
                              name={r.createdBy.name}
                              userId={r.createdBy.id}
                              avatarAt={r.createdBy.avatarAt}
                            />
                            <span>{r.createdBy.name}</span>
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="rq-stage">
                        <span className={`pill ${RFQ_STAGE[stage].pill}`}>{RFQ_STAGE[stage].label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="rq-empty">
              <p>
                {filtrat
                  ? t('Nicio cerere nu se potrivește.')
                  : grup === 'active'
                    ? t('Nu ai nimic de lucru. Creează o cerere de ofertă și alege furnizorii.')
                    : t('Nicio cerere aici.')}
              </p>
              {filtrat ? (
                <button
                  className="btn small"
                  onClick={() => {
                    setCauta('');
                    setSantier('');
                  }}
                >
                  {t('Șterge filtrele')}
                </button>
              ) : grup === 'active' ? (
                <button className="btn small primary" onClick={() => go({ view: 'new' })}>
                  {t('Cerere nouă')}
                </button>
              ) : null}
            </div>
          )
        ) : null}

        {cursor && !prima.loading ? (
          <div className="rq-more">
            <button className="btn small" disabled={aduc} onClick={() => void maiAdu()}>
              {aduc ? t('Se aduc…') : t('Arată mai multe ({0})', total - rows.length)}
            </button>
            {eroareMai ? <span className="muted">{eroareMai}</span> : null}
          </div>
        ) : null}
      </section>
    </>
  );
}
